import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { StackedAreaMix, AreaTrend, MIX_CLASS_COLORS } from "@/components/Charts";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { toastError } from "@/lib/toastError";
import { fmtCcy, fmtUSD, fmtUSDStrict, timeAgo } from "@/lib/format";
import { CardDeposit } from "@/components/wallet/CardDeposit";
import { CollectionAccounts } from "@/components/wallet/CollectionAccounts";
import { Payouts } from "@/components/wallet/Payouts";
import { MoneyTimeline } from "@/components/wallet/MoneyTimeline";
import { BalanceCheck } from "@/components/wallet/BalanceCheck";
import { ArrowLeftRight, Banknote, Download, Upload, Layers, LineChart as LineChartIcon, Share2, TrendingUp, TrendingDown, Copy, Globe2, SlidersHorizontal, Bell, Plus, Trash2, Landmark } from "lucide-react";
import { OwlLoaderBlock } from "@/components/OwlLoader";

const MIX_COLORS = MIX_CLASS_COLORS;

// Stable prop objects — recreating these on every render re-renders the charts for nothing.
const MIX_LABELS = { stock: "Stocks", commodity: "Commodities", forex: "Forex", crypto: "Crypto", cash: "Cash" };

const CARD_THEMES = [
  { id: "midnight", name: "Midnight", bg: "linear-gradient(135deg, #0B1E33 0%, #16324F 55%, #1E4A6B 100%)" },
  { id: "emerald", name: "Owl", bg: "linear-gradient(135deg, #120D22 0%, #33265A 55%, #4E3D79 100%)" },
  { id: "royal", name: "Royal", bg: "linear-gradient(135deg, #1B1436 0%, #35276B 55%, #4A3691 100%)" },
  { id: "graphite", name: "Graphite", bg: "linear-gradient(135deg, #14181D 0%, #232A33 55%, #333D49 100%)" },
];

export default function Wallet() {
  const { refreshUser, t } = useApp();
  const [wallet, setWallet] = useState(null);
  const [activity, setActivity] = useState([]);
  const [dep, setDep] = useState({ currency: "QAR", amount: 5000, method: "bank_transfer" });
  const [wd, setWd] = useState({ currency: "USD", amount: 500 });
  const [fx, setFx] = useState({ from_ccy: "QAR", to_ccy: "USD", amount: 10000 });
  const [busy, setBusy] = useState(false);
  const [mix, setMix] = useState(null);
  const [history, setHistory] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cardTheme, setCardTheme] = useState(CARD_THEMES[0].id);
  const [caption, setCaption] = useState("");
  const cardRef = useRef(null);
  // FX ticker (live rates) + personalization
  const [tickerRates, setTickerRates] = useState([]);
  const [tickerPairs, setTickerPairs] = useState(null);
  const [tickerOptions, setTickerOptions] = useState([]);
  // Rate Watch
  const [rateAlerts, setRateAlerts] = useState([]);
  const [rateForm, setRateForm] = useState({ from_ccy: "USD", to_ccy: "QAR", direction: "above", target: "" });
  // Open a new currency account
  const [openCcy, setOpenCcy] = useState("");

  const loadRateAlerts = useCallback(async () => {
    try { const { data } = await api.get("/fx/alerts"); setRateAlerts(data.alerts || []); }
    catch (e) { console.error(e); /* non-blocking */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [w, a, m, h, mo] = await Promise.all([
        api.get("/wallet"), api.get("/activity"), api.get("/portfolio/mix"),
        api.get("/portfolio/history?days=30"), api.get("/portfolio/monthly?months=6"),
      ]);
      setWallet(w.data); setActivity(a.data); setMix(m.data); setHistory(h.data); setMonthly(mo.data);
      setTickerPairs(w.data.ticker_pairs || []);
      setTickerOptions(w.data.ticker_options || []);
      loadRateAlerts();
    } catch (e) {
      toastError("Couldn't load your wallet", e, { id: "wallet-load", fallback: "Please refresh to try again." });
    }
  }, [loadRateAlerts]);
  useEffect(() => { load(); }, [load]);

  // Poll live ticker rates for the chosen pairs.
  const fetchTicker = async (pairs) => {
    if (!pairs || pairs.length === 0) { setTickerRates([]); return; }
    try {
      const { data } = await api.get("/market/fx", { params: { pairs: pairs.join(",") } });
      setTickerRates(data.rates || []);
    } catch (e) { console.error(e); /* keep last */ }
  };
  useEffect(() => {
    if (!tickerPairs) return;
    fetchTicker(tickerPairs);
    const iv = setInterval(() => fetchTicker(tickerPairs), 5000);
    return () => clearInterval(iv);
  }, [tickerPairs]);

  const toggleTickerPair = async (pair) => {
    const has = tickerPairs.includes(pair);
    const next = has ? tickerPairs.filter((p) => p !== pair) : [...tickerPairs, pair];
    setTickerPairs(next);
    try { await api.patch("/wallet/ticker-prefs", { pairs: next }); }
    catch (e) { toast.error("Could not save ticker"); }
  };

  const createRateAlert = async () => {
    if (!rateForm.target || Number(rateForm.target) <= 0) { toast.error("Enter a target rate"); return; }
    if (rateForm.from_ccy === rateForm.to_ccy) { toast.error("Currencies must differ"); return; }
    try {
      await api.post("/fx/alerts", { ...rateForm, target: Number(rateForm.target) });
      toast.success("Rate alert set", { description: `${rateForm.from_ccy}/${rateForm.to_ccy} ${rateForm.direction} ${rateForm.target}` });
      setRateForm({ ...rateForm, target: "" });
      loadRateAlerts();
    } catch (e) { toast.error("Could not set alert", { description: e?.response?.data?.detail }); }
  };
  const deleteRateAlert = async (id) => {
    try { await api.delete(`/fx/alerts/${id}`); loadRateAlerts(); toast.success("Rate alert removed"); }
    catch (e) { toast.error("Delete failed"); }
  };

  const exportCard = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: null, useCORS: true });
      const link = document.createElement("a");
      link.download = `tharwy-performance-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Performance card exported");
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const copyCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: null, useCORS: true });
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
          toast.success("Card copied to clipboard");
        } catch (e) {
          toast.error("Clipboard not supported here", { description: "Use Export instead" });
        }
      });
    } catch (e) { toast.error("Copy failed"); }
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      window.dispatchEvent(new Event("tharwy:deposit"));
      await Promise.all([load(), refreshUser()]);
    }
    catch (e) { toast.error("Failed", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const fxRate = wallet ? (wallet.fx_rates[fx.from_ccy] / wallet.fx_rates[fx.to_ccy]) : 1;
  const fxReceive = fx.amount * fxRate;
  const CCYS = wallet?.supported || ["USD", "QAR", "EUR", "GBP", "JPY", "SAR", "AED"];
  const accounts = wallet?.accounts || [];
  const openOptions = wallet?.open_options || [];

  // The allocation bar and its legend both walk the same list — do it once per change.
  const allocated = useMemo(
    () => (mix?.by_class || []).filter((c) => c.weight_pct > 0), [mix]);

  const openAccount = async () => {    if (!openCcy) { toast.error("Choose a currency to open"); return; }
    setBusy(true);
    try {
      await api.post("/wallet/open-account", { currency: openCcy });
      toast.success(`${openCcy} account opened`, { description: "You can now hold and receive funds in this currency." });
      setOpenCcy("");
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error("Could not open account", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <PageHeader title={t("wlt_title")} subtitle={t("wlt_subtitle")} testid="wallet-header" />
      {!wallet ? <OwlLoaderBlock label="Fetching your accounts…" /> : (
        <>
          {/* FX rate ticker */}
          <div className="mb-4 rounded-lg border bg-card/60 overflow-hidden" data-testid="wallet-fx-ticker">
            <div className="flex items-center gap-2 px-3 py-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground shrink-0 pe-2 border-e hover:text-foreground transition-colors" data-testid="fx-ticker-settings">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />FX
                    <SlidersHorizontal className="h-3 w-3 ms-0.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-56" data-testid="fx-ticker-picker">
                  <div className="text-xs font-medium mb-2">Ticker pairs</div>
                  <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-auto">
                    {tickerOptions.map((p) => (
                      <label key={p} className="flex items-center gap-1.5 text-xs cursor-pointer rounded px-1 py-0.5 hover:bg-accent" data-testid={`fx-pick-${p.replace("/", "")}`}>
                        <Checkbox checked={(tickerPairs || []).includes(p)} onCheckedChange={() => toggleTickerPair(p)} />
                        {p}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <div className="flex items-center gap-2 overflow-x-auto flex-1" style={{ scrollbarWidth: "none" }}>
                {tickerRates.length === 0 ? (
                  <span className="text-[11px] text-muted-foreground">No pairs selected — pick some via the FX menu.</span>
                ) : tickerRates.map((p) => {
                  const up = p.change_pct >= 0;
                  const decimals = p.rate >= 50 ? 2 : 4;
                  return (
                    <div key={p.pair} className="flex items-center gap-1.5 shrink-0 pe-3" data-testid={`fx-ticker-${p.from}${p.to}`}>
                      <span className="text-xs font-medium text-muted-foreground">{p.pair}</span>
                      <span className="text-xs font-semibold numeric">{p.rate.toFixed(decimals)}</span>
                      <span className={`inline-flex items-center text-[10px] numeric ${up ? "text-emerald-600" : "text-red-600"}`}>
                        {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{Math.abs(p.change_pct).toFixed(2)}%
                      </span>
                    </div>
                  );
                })}
                <span className="text-[10px] text-muted-foreground shrink-0 italic pe-1">indicative</span>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:gap-5 sm:grid-cols-3">
            <Card className="p-5 shadow-premium card-premium" data-testid="wallet-qar-balance">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("wlt_qar_balance")}</div>
              <div className="text-3xl font-semibold numeric mt-1">{fmtCcy(wallet.balances.QAR, "QAR")}</div>
            </Card>
            <Card className="p-5 shadow-premium card-premium" data-testid="wallet-usd-balance">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("wlt_usd_balance")}</div>
              <div className="text-3xl font-semibold numeric mt-1">{fmtUSDStrict(wallet.balances.USD)}</div>
            </Card>
            <Kpi label={t("wlt_total_usd")} value={fmtUSD(wallet.total_usd)} testid="wallet-total" />
          </div>

          {/* Multi-currency balances */}
          <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="wallet-currencies-card">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Globe2 className="h-4 w-4" />Currency balances</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {(wallet.supported || Object.keys(wallet.balances)).map((c) => {
                const bal = wallet.balances[c] || 0;
                const usd = (wallet.fx_rates?.[c] || 1) * bal;
                return (
                  <div key={c} className={`relative overflow-hidden rounded-lg border p-3 ${bal > 0 ? "" : "bg-secondary/40"}`} data-testid={`wallet-ccy-${c}`}>
                    {bal === 0 && (
                      <>
                        <img src="/tharwy-mark.png" alt="" aria-hidden="true"
                          className="absolute -bottom-2 -end-2 h-12 w-12 object-contain opacity-[0.13] dark:hidden" />
                        <img src="/tharwy-mark-light.png" alt="" aria-hidden="true"
                          className="absolute -bottom-2 -end-2 h-12 w-12 object-contain opacity-[0.16] hidden dark:block" />
                      </>
                    )}
                    <div className="text-[11px] font-semibold tracking-wide text-muted-foreground">{c}</div>
                    <div className={`numeric text-base font-semibold mt-0.5 ${bal === 0 ? "text-muted-foreground" : ""}`}>{fmtCcy(bal, c)}</div>
                    {c !== "USD" && bal > 0 && <div className="text-[10px] text-muted-foreground numeric">≈ {fmtUSDStrict(usd)}</div>}
                    {bal === 0 && <div className="text-[10px] text-muted-foreground">Waiting on its first deposit</div>}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Hold EUR, GBP, JPY and more so global trades settle in the local currency — no FX conversion each time.</p>
          </Card>

          {/* Open a currency account */}
          <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="open-account-card">
            <div className="flex items-center gap-2 text-sm font-medium mb-1"><Landmark className="h-4 w-4" />Open a currency account</div>
            <p className="text-[11px] text-muted-foreground mb-3">Open an account in your home currency or any market where you're based — receive salary, fund and withdraw locally.</p>
            <div className="mb-3 flex flex-wrap gap-1.5" data-testid="open-account-list">
              {accounts.map((c) => (
                <Badge key={c} variant="secondary" className="gap-1" data-testid={`account-open-${c}`}>{c} account</Badge>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-40">
                <Label className="text-xs">Currency</Label>
                <Select value={openCcy} onValueChange={setOpenCcy}>
                  <SelectTrigger data-testid="open-account-ccy"><SelectValue placeholder="Select currency" /></SelectTrigger>
                  <SelectContent className="max-h-64">{openOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={openAccount} disabled={busy || !openCcy} data-testid="open-account-submit"><Plus className="h-4 w-4 me-1" />Open account</Button>
              {openOptions.length === 0 && <span className="text-[11px] text-muted-foreground">You've opened accounts in every supported currency.</span>}
            </div>
          </Card>

          <CardDeposit />
          <CollectionAccounts onCredited={load} />
          <Payouts balances={wallet?.balances} onSent={load} />
          <MoneyTimeline />
          <BalanceCheck />

          {/* Rate Watch */}
          <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="rate-watch-card">
            <div className="flex items-center gap-2 text-sm font-medium mb-1"><Bell className="h-4 w-4" />Rate watch</div>
            <p className="text-[11px] text-muted-foreground mb-3">Get notified when a currency pair hits your target rate.</p>
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div className="w-20"><Label className="text-xs">From</Label>
                <Select value={rateForm.from_ccy} onValueChange={(v) => setRateForm({ ...rateForm, from_ccy: v })}>
                  <SelectTrigger data-testid="rate-from"><SelectValue /></SelectTrigger>
                  <SelectContent>{CCYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div className="w-20"><Label className="text-xs">To</Label>
                <Select value={rateForm.to_ccy} onValueChange={(v) => setRateForm({ ...rateForm, to_ccy: v })}>
                  <SelectTrigger data-testid="rate-to"><SelectValue /></SelectTrigger>
                  <SelectContent>{CCYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div className="w-28"><Label className="text-xs">When</Label>
                <Select value={rateForm.direction} onValueChange={(v) => setRateForm({ ...rateForm, direction: v })}>
                  <SelectTrigger data-testid="rate-direction"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="above">Rises above</SelectItem><SelectItem value="below">Falls below</SelectItem></SelectContent></Select></div>
              <div className="w-28"><Label className="text-xs">Target</Label>
                <Input type="number" step="0.0001" value={rateForm.target} onChange={(e) => setRateForm({ ...rateForm, target: e.target.value })} placeholder="rate" data-testid="rate-target" /></div>
              <Button onClick={createRateAlert} data-testid="rate-add"><Plus className="h-4 w-4 me-1" />Set alert</Button>
            </div>
            {rateAlerts.length === 0 ? (
              <EmptyState compact testid="rate-alerts-empty" title="No rate alerts yet" hint="We'll watch a pair overnight and ping you when it hits your rate." />
            ) : (
              <div className="space-y-1.5" data-testid="rate-alerts-list">
                {rateAlerts.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 rounded-lg border p-2.5 text-sm" data-testid={`rate-alert-${a.id}`}>
                    <Badge variant={a.status === "triggered" ? "secondary" : "outline"} className="text-[10px] capitalize">{a.status}</Badge>
                    <span className="font-medium">{a.from_ccy}/{a.to_ccy}</span>
                    <span className="text-muted-foreground text-xs">{a.direction === "above" ? "≥" : "≤"} {a.target}</span>
                    <span className="text-[11px] text-muted-foreground ms-auto numeric">now {a.current_rate}</span>
                    <button onClick={() => deleteRateAlert(a.id)} className="text-muted-foreground hover:text-red-600 transition-colors" data-testid={`rate-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {history && history.history?.length > 0 && (
            <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="mix-history-card">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium"><LineChartIcon className="h-4 w-4" />{t("wlt_mix_history")}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t("wlt_mix_history_sub")}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-end">
                    <div className="text-2xl font-semibold numeric">{fmtUSD(history.net_worth_usd)}</div>
                    <div className={`text-sm font-medium numeric ${history.change_usd >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {history.change_usd >= 0 ? "+" : ""}{fmtUSD(history.change_usd)} ({history.change_pct >= 0 ? "+" : ""}{history.change_pct}%)
                    </div>
                  </div>
                  {monthly && (
                    <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => setShareOpen(true)} data-testid="mix-share-btn">
                      <Share2 className="h-4 w-4" /> Share
                    </Button>
                  )}
                </div>
              </div>
              <StackedAreaMix data={history.history} keys={history.class_keys} height={260}
                labels={MIX_LABELS} />
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {history.class_keys.map((k) => (
                  <div key={k} className="flex items-center gap-1.5 text-xs" data-testid={`history-legend-${k}`}>
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: MIX_COLORS[k] || "#8A97A8" }} />
                    <span className="capitalize font-medium">{k}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {mix && (
            <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="portfolio-mix-card">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-2 text-sm font-medium"><Layers className="h-4 w-4" />{t("wlt_portfolio_mix")} — Stocks · Commodities · Forex · Crypto</div>
                <div className="text-end">
                  <div className="text-xs text-muted-foreground">{t("wlt_net_worth")}</div>
                  <div className="text-2xl font-semibold numeric">{fmtUSD(mix.net_worth_usd)}</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border p-3"><div className="text-[11px] uppercase text-muted-foreground">{t("mrb_invested")}</div><div className="numeric text-lg font-semibold">{fmtUSD(mix.invested_usd)}</div></div>
                <div className="rounded-lg border p-3"><div className="text-[11px] uppercase text-muted-foreground">{t("wlt_holdings_value")}</div><div className="numeric text-lg font-semibold">{fmtUSD(mix.holdings_value_usd)}</div></div>
                <div className="rounded-lg border p-3"><div className="text-[11px] uppercase text-muted-foreground">{t("wlt_total_pnl")}</div>
                  <div className={`numeric text-lg font-semibold ${mix.total_pnl_usd >= 0 ? "text-emerald-600" : "text-red-600"}`}>{mix.total_pnl_usd >= 0 ? "+" : ""}{fmtUSD(mix.total_pnl_usd)} <span className="text-xs">({mix.total_pnl_pct >= 0 ? "+" : ""}{mix.total_pnl_pct}%)</span></div>
                </div>
              </div>
              {/* Allocation bar */}
              <div className="mb-2 flex h-3 w-full overflow-hidden rounded-full" data-testid="mix-allocation-bar">
                {allocated.map((c) => (
                  <div key={c.asset_class} style={{ width: `${c.weight_pct}%`, background: MIX_COLORS[c.asset_class] || "#94A3B8" }} title={`${c.asset_class} ${c.weight_pct}%`} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
                {allocated.map((c) => (
                  <div key={c.asset_class} className="flex items-center gap-1.5 text-xs" data-testid={`mix-legend-${c.asset_class}`}>
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: MIX_COLORS[c.asset_class] || "#94A3B8" }} />
                    <span className="capitalize font-medium">{c.asset_class}</span>
                    <span className="text-muted-foreground">{c.weight_pct}% · {fmtUSD(c.current_usd)}</span>
                  </div>
                ))}
              </div>
              {mix.holdings.length > 0 && (
                <Table data-testid="mix-holdings-table"><TableHeader><TableRow><TableHead>{t("wlt_asset")}</TableHead><TableHead>{t("wlt_class")}</TableHead><TableHead className="text-end">{t("wlt_value")}</TableHead><TableHead className="text-end">{t("wlt_total_pnl")}</TableHead></TableRow></TableHeader>
                  <TableBody>{mix.holdings.slice(0, 8).map((h, hi) => (<TableRow key={`${h.symbol}-${hi}`}>
                    <TableCell><div className="font-medium">{h.symbol.split(".")[0]}</div><div className="text-xs text-muted-foreground truncate max-w-[160px]">{h.name}</div></TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{h.asset_class}</Badge></TableCell>
                    <TableCell className="text-end numeric">{fmtUSD(h.current_usd)}</TableCell>
                    <TableCell className={`text-end numeric ${h.pnl_usd >= 0 ? "text-emerald-600" : "text-red-600"}`}>{h.pnl_usd >= 0 ? "+" : ""}{fmtUSD(h.pnl_usd)} ({h.pnl_pct}%)</TableCell>
                  </TableRow>))}</TableBody></Table>
              )}
            </Card>
          )}

          <div className="grid gap-4 sm:gap-5 lg:grid-cols-3 mt-4 sm:mt-5">
            <Card className="p-5 shadow-premium">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><ArrowLeftRight className="h-4 w-4" />{t("wlt_fx_convert")}</div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">{t("wlt_from")}</Label>
                    <Select value={fx.from_ccy} onValueChange={(v) => setFx({ ...fx, from_ccy: v, to_ccy: v === fx.to_ccy ? fx.from_ccy : fx.to_ccy })}>
                      <SelectTrigger data-testid="fx-from"><SelectValue /></SelectTrigger>
                      <SelectContent>{CCYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-xs">{t("wlt_to")}</Label>
                    <Select value={fx.to_ccy} onValueChange={(v) => setFx({ ...fx, to_ccy: v, from_ccy: v === fx.from_ccy ? fx.to_ccy : fx.from_ccy })}>
                      <SelectTrigger data-testid="fx-to"><SelectValue /></SelectTrigger>
                      <SelectContent>{CCYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div><Label className="text-xs">{t("wlt_amount")}</Label><Input type="number" value={fx.amount} onChange={(e) => setFx({ ...fx, amount: Number(e.target.value) })} data-testid="fx-amount" /></div>
                <div className="rounded-lg bg-secondary/60 p-3 text-sm flex justify-between"><span className="text-muted-foreground">{t("wlt_you_receive")}</span><span className="numeric font-medium">{fmtCcy(fxReceive, fx.to_ccy)}</span></div>
                <Button className="w-full" disabled={busy} onClick={() => act(() => api.post("/wallet/fx", fx), "Conversion complete")} data-testid="wallet-fx-convert-submit">{t("wlt_convert")}</Button>
              </div>
            </Card>

            <Card className="p-5 shadow-premium">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Upload className="h-4 w-4" />{t("deposit")}</div>
              <div className="space-y-3">
                <div><Label className="text-xs">{t("wlt_currency")}</Label>
                  <Select value={dep.currency} onValueChange={(v) => setDep({ ...dep, currency: v })}><SelectTrigger data-testid="deposit-ccy"><SelectValue /></SelectTrigger><SelectContent>{CCYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">{t("wlt_amount")}</Label><Input type="number" value={dep.amount} onChange={(e) => setDep({ ...dep, amount: Number(e.target.value) })} data-testid="deposit-amount" /></div>
                <div><Label className="text-xs">{t("wlt_method")}</Label>
                  <Select value={dep.method} onValueChange={(v) => setDep({ ...dep, method: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="bank_transfer">{t("wlt_bank_transfer")}</SelectItem><SelectItem value="card">{t("wlt_card")}</SelectItem></SelectContent></Select></div>
                <Button className="w-full" disabled={busy} onClick={() => act(() => api.post("/wallet/deposit", dep), "Deposit successful")} data-testid="wallet-deposit-submit">{t("deposit")}</Button>
              </div>
            </Card>

            <Card className="p-5 shadow-premium">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Download className="h-4 w-4" />{t("withdraw")}</div>
              <div className="space-y-3">
                <div><Label className="text-xs">{t("wlt_currency")}</Label>
                  <Select value={wd.currency} onValueChange={(v) => setWd({ ...wd, currency: v })}><SelectTrigger data-testid="withdraw-ccy"><SelectValue /></SelectTrigger><SelectContent>{CCYS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">{t("wlt_amount")}</Label><Input type="number" value={wd.amount} onChange={(e) => setWd({ ...wd, amount: Number(e.target.value) })} data-testid="withdraw-amount" /></div>
                <Button variant="secondary" className="w-full" disabled={busy} onClick={() => act(() => api.post("/wallet/withdraw", { ...wd, destination: "bank" }), "Withdrawal requested")} data-testid="wallet-withdraw-submit">{t("wlt_withdraw_bank")}</Button>
                <p className="text-xs text-muted-foreground">Withdrawals settle to your linked bank account.</p>
              </div>
            </Card>
          </div>

          <Card className="p-5 shadow-premium mt-4 sm:mt-5">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Banknote className="h-4 w-4" />{t("wlt_transactions")}</div>
            {activity.length ? (
              <Table><TableHeader><TableRow><TableHead>{t("wlt_type")}</TableHead><TableHead>{t("wlt_detail")}</TableHead><TableHead className="text-end">{t("wlt_amount")}</TableHead><TableHead className="text-end">{t("wlt_time")}</TableHead></TableRow></TableHeader>
                <TableBody>{activity.map((a, i) => (<TableRow key={a.id || `${a.timestamp}-${a.type}-${i}`}>
                  <TableCell><Badge variant="secondary" className="capitalize">{(a.type || "").replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{a.symbol || a.portfolio || (a.from ? `${a.from} → ${a.to}` : a.method || a.destination || "-")}</TableCell>
                  <TableCell className="text-end numeric">{fmtCcy(a.amount, a.currency || "USD")}</TableCell>
                  <TableCell className="text-end text-xs text-muted-foreground">{timeAgo(a.timestamp)}</TableCell>
                </TableRow>))}</TableBody></Table>
            ) : <EmptyState title="No transactions" hint="Deposits, trades and transfers appear here" />}
          </Card>
        </>
      )}

      {/* Shareable monthly performance card */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mix-share-dialog">
          <DialogHeader><DialogTitle>Monthly Performance</DialogTitle>
            <DialogDescription>Your net worth trend over recent months — export it as a shareable card.</DialogDescription>
          </DialogHeader>
          {monthly && (() => {
            const up = monthly.period_change_pct >= 0;
            const maxV = Math.max(...monthly.months.map((m) => m.value)) || 1;
            const minV = Math.min(...monthly.months.map((m) => m.value)) || 0;
            const span = maxV - minV || 1;
            return (
              <>
                <div ref={cardRef} className="rounded-2xl p-6 text-white overflow-hidden relative"
                  style={{ background: (CARD_THEMES.find((th) => th.id === cardTheme) || CARD_THEMES[0]).bg }}
                  data-testid="mix-share-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src="/tharwy-mark-light.png" alt="" className="h-7 w-7 object-contain" />
                      <span className="font-semibold tracking-wide">Tharwy</span>
                    </div>
                    <span className="text-xs text-white/60">{monthly.current_month?.label}</span>
                  </div>
                  {caption && (
                    <div className="mt-3 text-sm text-white/85 leading-snug italic" data-testid="mix-share-caption">
                      &ldquo;{caption}&rdquo;
                    </div>
                  )}
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-widest text-white/60">Net Worth</div>
                    <div className="text-4xl font-semibold numeric mt-1">{fmtUSD(monthly.net_worth_usd)}</div>
                    <div className={`inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-sm font-medium ${up ? "bg-emerald-400/20 text-emerald-300" : "bg-red-400/20 text-red-300"}`}>
                      {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                      {up ? "+" : ""}{monthly.period_change_pct}% · {monthly.months.length}mo
                    </div>
                  </div>
                  <div className="mt-6 flex items-end gap-1.5 h-24">
                    {monthly.months.map((m) => {
                      const h = 20 + ((m.value - minV) / span) * 70;
                      return (
                        <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full rounded-t-sm" style={{ height: `${h}%`, background: "linear-gradient(180deg, var(--tharwy-lime) 0%, rgba(62,46,101,0.35) 100%)" }} />
                          <span className="text-[9px] text-white/55">{m.short}</span>
                        </div>
                      );
                    })}
                  </div>
                  {monthly.best_month && (
                    <div className="mt-4 flex items-center justify-between text-xs text-white/70 border-t border-white/10 pt-3">
                      <span>Best month</span>
                      <span className="font-medium text-white">{monthly.best_month.label} · {monthly.best_month.change_pct >= 0 ? "+" : ""}{monthly.best_month.change_pct}%</span>
                    </div>
                  )}
                  <div className="mt-1 text-[10px] text-white/40">Illustrative performance · tharwy.com</div>
                </div>
                <div className="flex items-center gap-2 mt-4" data-testid="card-theme-picker">
                  <span className="text-xs text-muted-foreground me-1">Theme</span>
                  {CARD_THEMES.map((th) => (
                    <button key={th.id} onClick={() => setCardTheme(th.id)}
                      className={`h-7 w-7 rounded-full border-2 transition-transform ${cardTheme === th.id ? "border-[color:var(--tharwy-lime)] scale-110" : "border-transparent"}`}
                      style={{ background: th.bg }} title={th.name} data-testid={`card-theme-${th.id}`} />
                  ))}
                </div>
                <div className="mt-3 space-y-1.5">
                  <Label className="text-xs">Caption (optional)</Label>
                  <Input value={caption} maxLength={120} onChange={(e) => setCaption(e.target.value)}
                    placeholder="Add a personal note to your card…" data-testid="mix-caption-input" />
                  <div className="text-[10px] text-muted-foreground text-end">{caption.length}/120</div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button variant="secondary" className="gap-2" onClick={copyCard} data-testid="mix-copy-btn">
                    <Copy className="h-4 w-4" /> Copy
                  </Button>
                  <Button className="gap-2" onClick={exportCard} disabled={exporting} data-testid="mix-export-btn">
                    <Download className="h-4 w-4" /> {exporting ? "Exporting..." : "Export"}
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
